import React from 'react';

import { type Shown, delta, formatExact, formatValue, shortDay } from '../format';
import type { Block, Envelope, EnvelopeColumn, KpiItem } from '../types';

import Spark from './Spark';

type FiguresProps = {
  block: Block;
  envelopes: Array<Envelope>;
  selected?: string;
  onSelect?: (measure: string) => void;
  picked?: string;
  lifted?: string;
  width: number;
  shown: Shown;
};

const MIN_TILE = 220;
const MIN_PLAIN_TILE = 150;
const STATUS_MOVE = 0.05;

function columnsWithoutOrphans(cells: number, width: number, largest: number, minTile: number): number {
  const most = Math.max(largest, 1, Math.min(cells, Math.floor(width / minTile)));
  let best = most;
  let fewestEmpty = Infinity;
  for (let count = most; count >= Math.max(largest, Math.ceil(most / 2)); count--) {
    const empty = (count - (cells % count)) % count;
    if (empty < fewestEmpty) {
      best = count;
      fewestEmpty = empty;
    }
  }
  return Math.min(best, cells);
}

type FigureProps = {
  item: KpiItem;
  picked: boolean;
  lifted: boolean;
  span?: number;
  column: EnvelopeColumn;
  envelope: Envelope;
  large: boolean;
  selected?: string;
  onSelect?: (measure: string) => void;
  shown: Shown;
  against?: Array<{ label: string; base: number | null | undefined }>;
};

function bestOf(points: Array<{ from: string; to: string; v: number | null }>, lowerIsBetter: boolean) {
  const known = points.filter((point) => point.v !== null);
  if (known.length < 2) return undefined;
  return known.reduce((best, point) => ((lowerIsBetter ? point.v! < best.v! : point.v! > best.v!) ? point : best));
}

function Figure({ item, picked, lifted, span, column, envelope, large, selected, onSelect, shown: blockShown, against = [] }: FigureProps) {
  const shown: Shown = { delta: item.delta ?? blockShown.delta, baseline: item.baseline ?? blockShown.baseline, numbers: item.numbers ?? blockShown.numbers };
  const label = item.label ?? column.label;
  const number = (value: number | null | undefined) => formatValue(value, column.format, { numbers: shown.numbers, decimals: column.decimals });
  const value = envelope.totals?.current?.[column.name] as number | null | undefined;
  const sample = envelope.totals?.current?.[`n:${column.name}`] as number | null | undefined;
  const thin = column.minN !== undefined && sample !== null && sample !== undefined && sample < column.minN;
  const comparisons = envelope.period.comparisons.map((window) => {
    const base = envelope.totals?.[window.id]?.[column.name] as number | null | undefined;
    return { window, base, change: thin ? undefined : delta(value, base, column, shown.delta, shown.numbers) };
  });
  const showsComparisons = (shown.delta !== 'none' || shown.baseline) && (value !== null && value !== undefined || comparisons.some((entry) => entry.base !== null && entry.base !== undefined));
  const tones = comparisons.map((entry) => entry.change?.tone).filter(Boolean);
  const moved = comparisons.length > 0 && comparisons.every((entry) => entry.change?.relative !== undefined && Math.abs(entry.change.relative) >= STATUS_MOVE);
  const status = item.status === 'none' ? '' : tones.length && moved && tones.every((tone) => tone === 'up') ? 'good' : tones.length && moved && tones.every((tone) => tone === 'down') ? 'bad' : '';
  const ref = `${envelope.query}.${column.name}`;
  const spark = item.spark === false ? undefined : envelope.spark?.[column.name];
  const best = large && spark ? bestOf(spark, Boolean(column.lowerIsBetter)) : undefined;
  const className = ['fig', large ? 'lg' : '', spark ? '' : 'plain', status, item.status === 'border' ? 'status' : '', onSelect ? 'tab' : '', selected === column.name ? 'on' : '', picked ? 'picked' : '', lifted ? 'dragging' : ''].filter(Boolean).join(' ');
  const body = (
    <>
      <div className="l" title={[label, column.definition].filter(Boolean).join(': ')}>
        {label}
      </div>
      <div className={`v${thin ? ' thin' : ''}`} data-lens-ref={ref} title={formatExact(value, column.format)}>
        {number(value)}
        {thin ? <span className="n">n={sample}</span> : null}
      </div>
      {spark ? <Spark large={large} points={spark.map((point) => point.v)} /> : null}
      <div className="ds">
        {(showsComparisons ? comparisons : []).map(({ window, base, change }) => (
          <div className="d" data-lens-ref={`${ref}@${window.id}`} key={window.id} title={shown.baseline ? `${`vs ${window.label}`}
: ${number(base)}` : undefined}>
            <span>
              {shown.delta === 'none' ? null : change ? (
                <b className={change.tone} data-lens-ref={`${ref}@${window.id}`}>
                  {change.arrow} {change.text}
                </b>
              ) : (
                '–'
              )}{' '}
              {`vs ${COMPARE_WORDS[window.id] ?? window.label}`}

            </span>{' '}
            {shown.baseline ? (
              <span>
                {shown.delta === 'none' ? '' : '· '}
                <span data-lens-ref={`${ref}@${window.id}`} title={formatExact(base, column.format)}>
                  {number(base)}
                </span>
              </span>
            ) : null}
          </div>
        ))}
        {(showsComparisons ? against : []).map(({ label, base }) => {
          const change = thin ? undefined : delta(value, base, column, shown.delta, shown.numbers);
          return (
            <div className="d" data-lens-ref={ref} key={label}>
              <span>
                {change ? <b className={change.tone}>{change.arrow} {change.text}</b> : shown.delta === 'none' ? '' : '–'} vs {label}
              </span>
              {shown.baseline ? <span> · {number(base)}</span> : null}
            </div>
          );
        })}
      </div>
      {best ? (
        <div className="fact">
          {best === spark![spark!.length - 1]
            ? `Best of the last ${spark!.length} periods`
            : `Best of the last ${spark!.length} periods: ${shortDay(best.from)} – ${shortDay(best.to)}, ${number(best.v)}`}
        </div>
      ) : null}
    </>
  );
  if (!onSelect)
    return (
      <div className={className} data-lens-item={item.kpi} style={span ? { gridColumn: `span ${span}` } : undefined}>
        {body}
      </div>
    );
  return (
    <button aria-pressed={selected === column.name} className={className} data-lens-item={item.kpi} style={span ? { gridColumn: `span ${span}` } : undefined} onClick={() => onSelect(column.name)} type="button">
      {body}
    </button>
  );
}

const COMPARE_WORDS: Record<string, string> = { prior: 'the prior period', ly: 'last year', l2y: 'two years ago' };

export default function Figures({ block, envelopes, selected, onSelect, picked, lifted, width, shown }: FiguresProps) {
  const owners = new Map(envelopes.flatMap((envelope) => envelope.columns.map((column) => [column.name, { column, envelope }] as const)));
  const find = (kpi: string) => owners.get(kpi) ?? [...owners.entries()].find(([name]) => name.endsWith(`.${kpi}`) || kpi.endsWith(`.${name}`))?.[1];
  const items = ((block.kpis ?? (block.kpi ? [block.kpi] : [])) as Array<string | KpiItem>)
    .map((entry) => (typeof entry === 'string' ? { kpi: entry } : entry))
    .filter((item) => find(item.kpi));
  const wide = width >= 2 * MIN_TILE + 8;
  const large = (item: KpiItem) => wide && (item.w === 2 || item.size === 'lg');
  const cells = items.reduce((sum, item) => sum + (large(item) ? 4 : 1), 0);
  const sparked = envelopes.some((envelope) => Object.keys(envelope.spark ?? {}).length > 0);
  const count = Math.max(Math.min(2, cells), columnsWithoutOrphans(cells, width, items.some(large) ? 2 : 1, sparked ? MIN_TILE : MIN_PLAIN_TILE));
  const lone = cells === 1 && width >= 4 * MIN_TILE;
  const tail = items.some(large) || cells <= count ? 0 : cells % count;
  return (
    <div className="figs" data-cols={count} style={{ gridTemplateColumns: lone ? 'minmax(0, 25%)' : `repeat(${tail ? count * tail : count}, minmax(0, 1fr))` }}>
      {items.map((item, index) => {
        const owner = find(item.kpi);
        return owner ? (
          <Figure
            against={(item.against ?? []).map((other) => ({ label: other.label ?? other.kpi, base: find(other.kpi)?.envelope.totals?.current?.[find(other.kpi)!.column.name] as number | null | undefined }))}
            column={owner.column}
            envelope={owner.envelope}
            item={item}
            key={item.kpi}
            lifted={lifted === item.kpi}
            picked={picked === item.kpi}
            span={tail ? (index >= items.length - tail ? count : tail) : undefined}
            large={large(item)}
            onSelect={onSelect}
            selected={selected}
            shown={shown}
          />
        ) : null;
      })}
    </div>
  );
}
